import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErroApi } from '../lib/api';
import {
  buscarCatalogo,
  buscarRepertorio,
  criarMusica,
  removerMusica,
  ultimaVezLegivel,
  type Musica,
  type MusicaCatalogo,
} from '../lib/midias';
import { Alerta, Botao, Cartao } from './Interface';
import { assistirNoYouTube, capaDoYouTube } from '../lib/youtube';

/** Compara titulo como o servidor compara: sem caixa e sem espaco repetido. */
function chave(titulo: string): string {
  return titulo.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Repertorio do culto — existe apenas no ministerio de Louvor. */
export default function Repertorio({ eventoId }: { eventoId: number }) {
  const [musicas, setMusicas] = useState<Musica[]>([]);
  const [podeEditar, setPodeEditar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [tom, setTom] = useState('');
  const [link, setLink] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [catalogo, setCatalogo] = useState<MusicaCatalogo[]>([]);

  // o que o catalogo ja sabe sobre o que esta sendo digitado
  const conhecida = useMemo(
    () => catalogo.find((c) => chave(c.titulo) === chave(nome)) ?? null,
    [catalogo, nome],
  );

  const carregar = useCallback(async () => {
    try {
      const [d, cat] = await Promise.all([buscarRepertorio(eventoId), buscarCatalogo()]);
      setMusicas(d.musicas);
      setPodeEditar(d.podeEditar);
      setCatalogo(cat.musicas);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar o repertório.');
    }
  }, [eventoId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function adicionar() {
    if (!nome.trim()) return;
    setSalvando(true);
    setErro(null);
    try {
      await criarMusica({
        eventoId,
        // reconhecida: aponta para o catalogo em vez de criar outra entrada
        ...(conhecida ? { musicaId: conhecida.id } : {}),
        musica: nome.trim(),
        tom: tom.trim() || null,
        link: link.trim() || null,
      });
      setNome('');
      setTom('');
      setLink('');
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui adicionar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Cartao className="mt-4">
      <h3 className="font-semibold text-tinta-900">
        Repertorio <span className="font-normal text-tinta-400">({musicas.length})</span>
      </h3>
      <p className="text-xs text-tinta-400">
        As musicas do culto. Quem esta escalado ve aqui para ensaiar.
      </p>

      {erro && (
        <div className="mt-3">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      {musicas.length === 0 ? (
        <p className="mt-3 text-sm text-tinta-400">Nenhuma música escolhida ainda.</p>
      ) : (
        <ol className="mt-3 space-y-2">
          {musicas.map((m, i) => {
            const capa = capaDoYouTube(m.link);
            const assistir = assistirNoYouTube(m.link);
            return (
              <li key={m.id} className="flex items-center gap-3 rounded-xl bg-tinta-100 p-2">
                {capa ? (
                  <a
                    href={assistir ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    title="Assistir no YouTube"
                    className="relative block h-14 w-24 shrink-0 overflow-hidden rounded-lg bg-white"
                  >
                    <img src={capa} alt="" loading="lazy" className="h-full w-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-tinta-900/25 text-white">
                      ▶
                    </span>
                  </a>
                ) : (
                  <span className="flex h-14 w-24 shrink-0 items-center justify-center rounded-lg bg-white text-lg text-tinta-400">
                    ♪
                  </span>
                )}

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-tinta-900">
                    <span className="text-tinta-400">{i + 1}. </span>
                    {m.musica}
                  </span>
                  <span className="block truncate text-xs text-tinta-400">
                    {m.artista ?? 'Sem artista'}
                    {m.tom ? ` · Tom ${m.tom}` : ''}
                  </span>
                  {assistir && (
                    <a
                      href={assistir}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs font-semibold text-tinta-900 hover:underline"
                    >
                      assistir
                    </a>
                  )}
                </span>

                {podeEditar && (
                  <button
                    onClick={async () => {
                      try {
                        await removerMusica(m.id);
                        await carregar();
                      } catch (e) {
                        setErro(e instanceof ErroApi ? e.message : 'Não consegui remover.');
                      }
                    }}
                    className="shrink-0 text-xs text-tinta-400 hover:text-red-600"
                  >
                    tirar
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {podeEditar && (
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_5rem_1fr_auto]">
          <input
            value={nome}
            list="catalogo-de-musicas"
            onChange={(e) => setNome(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void adicionar()}
            placeholder="Nome da música"
            className="rounded-xl border border-tinta-200 px-3 py-2 text-sm"
          />
          <input
            value={tom}
            onChange={(e) => setTom(e.target.value)}
            placeholder="Tom"
            className="rounded-xl border border-tinta-200 px-3 py-2 text-sm"
          />
          <input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="Link da cifra ou vídeo"
            className="rounded-xl border border-tinta-200 px-3 py-2 text-sm"
          />
          <Botao carregando={salvando} onClick={() => void adicionar()}>
            Adicionar
          </Botao>
        </div>
      )}

      {/* autocompletar nativo: sem componente novo e funciona no celular */}
      {podeEditar && (
        <datalist id="catalogo-de-musicas">
          {catalogo.map((c) => (
            <option key={c.id} value={c.titulo}>
              {[c.artista, c.tom ? `tom ${c.tom}` : null].filter(Boolean).join(' · ')}
            </option>
          ))}
        </datalist>
      )}

      {podeEditar && conhecida && (
        <p className="mt-2 text-xs text-tinta-400">
          Ja no repertorio da igreja
          {conhecida.tom ? ` · costuma ser em ${conhecida.tom}` : ''}
          {conhecida.vezes > 0
            ? ` · cantada ${conhecida.vezes}x${
                ultimaVezLegivel(conhecida.ultimaVez)
                  ? `, a ultima em ${ultimaVezLegivel(conhecida.ultimaVez)}`
                  : ''
              }`
            : ' · ainda não cantada'}
        </p>
      )}

      {podeEditar && !conhecida && nome.trim() !== '' && (
        <p className="mt-2 text-xs text-tinta-400">
          Musica nova — vai entrar no repertorio da igreja ao ser adicionada.
        </p>
      )}
    </Cartao>
  );
}
